import {
  AUDIT_CATEGORIES,
  isAuditCategory,
  type AuditCategory,
} from '@promanage/core/audit-categories';
import { isCalendarDate } from '@promanage/core';

/**
 * A log report's filter, as it lives in the URL.
 *
 * TWO REPORTS READ THIS. The audit log and the domain event log are different tables answering
 * different questions, but they are asked the same way — a range of days, some categories, one
 * person — and the URL is a contract with whoever pasted it into an email. Two copies of this
 * parse would be two chances for `?categories=` to mean something slightly different on two pages
 * that look identical, which is the kind of divergence nobody finds until a bookmark stops working.
 *
 * SEPARATE FROM `logFilter.ts`, AND THE SPLIT IS A BUNDLE DECISION. `validateSearch` runs during
 * ROUTING — TanStack's `autoCodeSplitting` moves a route's `component` into its own chunk and
 * leaves everything else in the eager route tree — so whatever this file imports is on every page
 * of the app. It therefore imports the category LIST and nothing else; the sixty event labels
 * beside it in `@promanage/core/audit` are 2 KB that belong on each report's own chunk.
 *
 * The same trap the design system records for `resolvePersonRef` pulling zod eager. Anything added
 * to this file lands in the initial bundle, so add it to `logFilter.ts` instead unless routing
 * genuinely needs it.
 */
export interface LogFilter {
  from?: string;
  to?: string;
  /** Absent means every category, which is not the same as an empty array. */
  categories?: AuditCategory[];
  actor?: string;
}

/**
 * Read the filter out of the URL.
 *
 * SHAPE-CHECKED ONLY, deliberately — the same call `/financial/transactions` makes. A malformed
 * range is refused by the contract, which is the boundary the pasted URL crosses anyway, and the
 * server's sentence beside the filter is a better answer than a router error page.
 *
 * An EMPTY category list becomes "no filter" rather than "show nothing". `?categories=` with
 * nothing after it is a URL somebody edited or a control that lost its state, and the honest
 * reading of a filter with nothing in it is that nothing is being filtered.
 */
export function parseLogSearch(search: Record<string, unknown>): LogFilter {
  const categories = parseCategories(search.categories);

  return {
    ...(typeof search.from === 'string' && isCalendarDate(search.from)
      ? { from: search.from }
      : {}),
    ...(typeof search.to === 'string' && isCalendarDate(search.to) ? { to: search.to } : {}),
    ...(categories.length > 0 ? { categories } : {}),
    ...(typeof search.actor === 'string' && search.actor !== '' ? { actor: search.actor } : {}),
  };
}

/** Categories arrive as a comma-separated list, or as an array from a typed `<Link>`. */
function parseCategories(raw: unknown): AuditCategory[] {
  const parts = Array.isArray(raw)
    ? raw
    : typeof raw === 'string'
      ? raw.split(',')
      : ([] as unknown[]);

  const chosen = parts.filter(
    (part): part is AuditCategory => typeof part === 'string' && isAuditCategory(part),
  );

  // Every category selected is the same question as none, and the URL should say so — otherwise
  // ticking the last box leaves a long parameter that means "no filter".
  return chosen.length === AUDIT_CATEGORIES.length ? [] : [...new Set(chosen)];
}
